/**
 * Creates a comprehensive table change handler that separates pagination and sorting logic
 *
 * This handler provides clean separation between pagination and sorting actions,
 * preventing conflicts that can occur when both events are triggered simultaneously.
 * It uses Antd's action parameter to determine the type of change.
 *
 * @param {Function} onPageChange - Callback function for pagination changes
 * @param {Function} onSortChange - Callback function for sorting changes
 * @param {Array} columns - Table columns configuration for key mapping
 * @param {boolean} [debug=false] - Enable debug logging for development
 * @returns {Function} Optimized table change handler function
 *
 * @example
 * const handleTableChange = createTableChangeHandler(
 *   ({ page, size }) => updatePagination({ page, size }),
 *   (field, order) => updateSorting(field, order),
 *   tableColumns
 * );
 */
export const createTableChangeHandler = (
  onPageChange,
  onSortChange,
  columns = []
) => {
  return (pagination, filters, sorter, extra) => {
    const action = extra?.action;

    if (action === 'paginate' || (!action && pagination)) {
      onPageChange?.({
        page: pagination.current,
        size: pagination.pageSize,
      });
    } else if (action === 'sort') {
      if (sorter?.field) {
        const order =
          sorter.order === 'ascend'
            ? 'asc'
            : sorter.order === 'descend'
              ? 'desc'
              : '';

        const sortField = resolveSortField(sorter, columns);
        onSortChange?.(sortField, order);
      } else {
        onSortChange?.('', '');
      }
    }
  };
};

/**
 * Resolves the correct sort field key from sorter information
 *
 * Handles complex scenarios where multiple columns share the same dataIndex
 * but have different keys. Prioritizes the actual clicked column over field matching.
 *
 * @param {Object} sorter - Antd sorter object containing field and column info
 * @param {Array} columns - Table columns configuration
 * @returns {string} The resolved field key for sorting
 *
 * @example
 * const sortField = resolveSortField(sorter, columns);
 * // Returns 'chemical_families' instead of 'pesticide_group_associations'
 */
export const resolveSortField = (sorter, columns) => {
  if (sorter.column?.key) {
    return sorter.column.key;
  }

  const column =
    columns.find((col) => col.key === sorter.field) ||
    columns.find((col) => col.dataIndex === sorter.field);

  return column?.key || sorter.field;
};

/**
 * Creates a function to calculate current page keys for expanded rows
 *
 * This utility helps manage which rows should be expanded based on
 * the current page, improving performance by only expanding visible items.
 *
 * @param {number} pageSize - Number of items displayed per page
 * @returns {Function} Function that calculates page keys
 *
 * @example
 * const getCurrentPageKeys = createCurrentPageKeysGetter(20);
 * const pageKeys = getCurrentPageKeys(2, allItems); // Returns keys for page 2
 */
export const createCurrentPageKeysGetter = (pageSize) => {
  return (page, items) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return items.slice(startIndex, endIndex).map((item) => item.id);
  };
};

/**
 * Creates bidirectional scroll synchronization between table elements
 *
 * Ensures that horizontal scrolling is synchronized between table header
 * and body sections, maintaining alignment during user interaction.
 *
 * @param {HTMLElement} tableHeader - Header element to sync
 * @param {HTMLElement} tableBody - Body element to sync
 * @returns {Function} Cleanup function to remove event listeners
 *
 * @example
 * useEffect(() => {
 *   const cleanup = createScrollSync(headerRef.current, bodyElement);
 *   return cleanup;
 * }, []);
 */
export const createScrollSync = (tableHeader, tableBody) => {
  if (!tableBody || !tableHeader) {
    return () => {};
  }

  const syncScroll = (source, target) => () => {
    target.scrollLeft = source.scrollLeft;
  };

  const handleTableScroll = syncScroll(tableBody, tableHeader);
  const handleHeaderScroll = syncScroll(tableHeader, tableBody);

  tableBody.addEventListener('scroll', handleTableScroll);
  tableHeader.addEventListener('scroll', handleHeaderScroll);

  return () => {
    tableBody.removeEventListener('scroll', handleTableScroll);
    tableHeader.removeEventListener('scroll', handleHeaderScroll);
  };
};

/**
 * Creates standardized pagination configuration for Antd Tables
 *
 * Provides consistent pagination behavior across different table implementations
 * with internationalization support and customizable options.
 *
 * @param {Object} data - API response data containing pagination info
 * @param {number} pageSize - Items per page
 * @param {Function} t - Internationalization function
 * @param {Object} [options={}] - Additional pagination configuration
 * @param {boolean} [options.showSizeChanger=true] - Show page size selector
 * @param {boolean} [options.showQuickJumper=false] - Show quick page jumper
 * @param {string} [options.className='custom-pagination'] - CSS class name
 * @param {boolean} [options.hideOnSinglePage=true] - Hide when only one page
 * @returns {Object} Complete Antd pagination configuration
 *
 * @example
 * const pagination = createPaginationConfig(data, 20, t, {
 *   showQuickJumper: true,
 *   showSizeChanger: false
 * });
 */
export const createPaginationConfig = (data, pageSize, t, options = {}) => {
  const {
    showSizeChanger = true,
    showQuickJumper = true,
    className = 'custom-pagination',
    hideOnSinglePage = false,
    pageSizeOptions = ['10', '20', '50'],
    ...restOptions
  } = options;

  return {
    current: data?.page || 1,
    pageSize,
    className,
    total: data?.count || 0,
    size: 'default',
    showSizeChanger,
    showQuickJumper,
    showTotal: (total, range) =>
      total === 0
        ? `0 ${t('common|result', 'résultat')}`
        : `${range[0]}-${range[1]} ${t('active-substance|of')} ${total} ${total > 1 ? t('common|results', 'résultats') : t('common|result', 'résultat')}`,
    hideOnSinglePage,
    pageSizeOptions,
    ...restOptions,
    // position: ['top', 'bottom'],
  };
};

/**
 * Global drag-to-scroll for Ant Design tables.
 *
 * Uses document-level event delegation so every `.ant-table-container`
 * element automatically supports click-and-drag horizontal scrolling
 * on desktop (mouse users). Initialises once on first import.
 */
(() => {
  if (typeof window === 'undefined') return;

  let scrollEl = null;
  let startX = 0;
  let scrollLeft = 0;

  const INTERACTIVE = 'a, button, input, .ant-checkbox, .ant-switch, .ant-select, .ant-pagination';

  document.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return; // left-click only
    if (e.target.closest(INTERACTIVE)) return;

    const el = e.target.closest('.ant-table-content');
    if (!el || el.scrollWidth <= el.clientWidth) return; // not scrollable

    scrollEl = el;
    startX = e.pageX;
    scrollLeft = el.scrollLeft;
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  });

  window.addEventListener('mousemove', (e) => {
    if (!scrollEl) return;
    e.preventDefault();
    scrollEl.scrollLeft = scrollLeft - (e.pageX - startX);
  });

  window.addEventListener('mouseup', () => {
    if (!scrollEl) return;
    scrollEl.style.cursor = '';
    scrollEl.style.removeProperty('user-select');
    scrollEl = null;
  });
})();
